import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Switch,
  message,
  Space,
  Tag,
  Tooltip,
  Typography,
  Card,
  Row,
  Col,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  KeyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  CopyOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import {
  getApplicantsFilter,
  adminResetUserPassword,
  Applicant,
  ApplicantFilterParams,
} from './userPasswordReset.api';
import { getAdminUsers } from '../request';

const { Title, Text } = Typography;

type ResettableUser = Applicant | AdminUser;

const PAGE_SIZE = 10;

// Generates a strong random password e.g. " (MpgDT6+#B" style — mixed case, digits, symbols
const generateStrongPassword = (length = 12): string => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const symbols = '!@#$%^&*()-_+=';
  const all = upper + lower + digits + symbols;

  const required = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ];

  const rest = Array.from(
    { length: length - required.length },
    () => all[Math.floor(Math.random() * all.length)]
  );

  return [...required, ...rest]
    .sort(() => Math.random() - 0.5)
    .join('');
};

interface ResetUserModalProps {
  open: boolean;
  applicant: ResettableUser | null;
  defaultIsAdmin: boolean;
  onClose: () => void;
}

const ResetPasswordModal: React.FC<ResetUserModalProps> = ({
  open,
  applicant,
  defaultIsAdmin,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [showPassword, setShowPassword] = useState(false);
  const [isAdmin, setIsAdmin] = useState(defaultIsAdmin);

  useEffect(() => {
    setIsAdmin(defaultIsAdmin);
    if (!open) {
      form.resetFields();
      setShowPassword(false);
    }
  }, [defaultIsAdmin, form, open]);

  const { mutate: resetPassword, isPending } = useMutation({
    mutationFn: adminResetUserPassword,
    onSuccess: (data) => {
      message.success(data?.message || data?.Message || 'Password reset successfully');
      handleClose();
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.message ||
          error?.response?.data?.Message ||
          'Failed to reset password'
      );
    },
  });

  const handleClose = () => {
    form.resetFields();
    setShowPassword(false);
    setIsAdmin(false);
    onClose();
  };

  const handleGenerate = () => {
    const generated = generateStrongPassword();
    form.setFieldsValue({ password: generated, confirmPassword: generated });
    setShowPassword(true);
  };

  const handleCopy = () => {
    const pwd = form.getFieldValue('password');
    if (pwd) {
      navigator.clipboard.writeText(pwd);
      message.success('Password copied to clipboard');
    }
  };

  const getApplicantName = (user?: ResettableUser | null): string => {
    if (!user) return '';
    return [user.firstName, user.lastName].filter(Boolean).join(' ');
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (!applicant) return;

    resetPassword({
      password: values.password,
      confirmPassword: values.confirmPassword,
      email: applicant.email || '',
      applicantId: String(applicant.applicantId),
      isAdmin,
    });
  };

  return (
    <Modal
      open={open}
      // style={{backgroundColor: '#8F0024' }}
      onCancel={handleClose}
      title={
        <span style={{ color: '#fff'}}>
          <KeyOutlined style={{ marginRight: 8 }} />
          Reset User Password
        </span>
        
      }
      styles={{
        header: { backgroundColor: 'darkgreen', padding: '16px 24px', marginBottom: 0 },
        content: { padding: 0, overflow: 'hidden', borderRadius: 8, },
      }}
      footer={null}
      destroyOnClose
      width={480}
    >
      <div style={{ padding: '24px' }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            applicantId: applicant?.applicantId,
            email: applicant?.email,
          }}
        >
          <Form.Item label="Applicant ID">
            <Input value={applicant?.applicantId} disabled />
          </Form.Item>

          <Form.Item label="Name">
            <Input value={getApplicantName(applicant)} disabled />
          </Form.Item>

          <Form.Item label="Email">
            <Input value={applicant?.email} disabled />
          </Form.Item>

          <Form.Item label="Reset as Admin user" style={{ marginBottom: 12 }}>
            <Space align="center">
              <Switch checked={isAdmin} onChange={setIsAdmin} />
              <Text type="secondary">
                {isAdmin ? 'Resetting as Admin account' : 'Resetting as Student account'}
              </Text>
            </Space>
          </Form.Item>

          <Form.Item
            label={
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <span>New Password</span>
              </Space>
            }
            name="password"
            rules={[
              { required: true, message: 'Password is required' },
              { min: 8, message: 'Password must be at least 8 characters' },
            ]}
          >
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter or generate a password"
              suffix={
                <Space size={4}>
                  <Tooltip title={showPassword ? 'Hide' : 'Show'}>
                    <Button
                      type="text"
                      size="small"
                      icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                      onClick={() => setShowPassword((s) => !s)}
                    />
                  </Tooltip>
                  <Tooltip title="Copy">
                    <Button type="text" size="small" icon={<CopyOutlined />} onClick={handleCopy} />
                  </Tooltip>
                </Space>
              }
            />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm the password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input type={showPassword ? 'text' : 'password'} placeholder="Confirm password" />
          </Form.Item>

          <Button
            type="dashed"
            icon={<ThunderboltOutlined />}
            onClick={handleGenerate}
            block
            style={{ marginBottom: 20 }}
          >
            Generate Strong Password
          </Button>

          <Row gutter={12}>
            <Col span={12}>
              <Button block onClick={handleClose}>
                Cancel
              </Button>
            </Col>
            <Col span={15}>
              <Button
                type="primary"
                block
                loading={isPending}
                onClick={handleSubmit}
                style={{ backgroundColor: 'darkgreen', borderColor: 'darkgreen' }}
              >
                Reset Password
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

const ResetAdminPassword: React.FC = () => {
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useState<Partial<ApplicantFilterParams>>({});
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedApplicant, setSelectedApplicant] = useState<ResettableUser | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalDefaultIsAdmin, setModalDefaultIsAdmin] = useState(false);

  const applicantQuery = useQuery({
    queryKey: ['applicants-filter', pageNumber, searchParams],
    queryFn: () =>
      getApplicantsFilter({
        PageNumber: pageNumber,
        PageSize: PAGE_SIZE,
        ...searchParams,
      }),
  });

  const adminUsersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: getAdminUsers,
  });

  const applicants = useMemo<ResettableUser[]>(() => applicantQuery.data?.items ?? [], [applicantQuery.data]);
  const adminUsers = useMemo<AdminUser[]>(() => adminUsersQuery.data ?? [], [adminUsersQuery.data]);

  const handleSearch = () => {
    const trimmed = searchTerm.trim();
    setPageNumber(1);

    if (!trimmed) {
      setSearchParams({});
      return;
    }

    if (trimmed.includes('@')) {
      setSearchParams({ Email: trimmed });
    } else if (/^[0-9a-fA-F-]{36}$/.test(trimmed)) {
      setSearchParams({ ApplicantId: trimmed });
    } else if (/^(App\/|APP\/|app\/)/.test(trimmed) || /\d/.test(trimmed)) {
      setSearchParams({ ApplicationNumber: trimmed });
    } else {
      setSearchParams({ Name: trimmed });
    }
  };

  const handleReset = () => {
    setSearchTerm('');
    setSearchParams({});
    setPageNumber(1);
  };

  const openResetModal = (record: ResettableUser) => {
    setSelectedApplicant(record);
    const isAdminDefault = 'roleList' in record ? true : Boolean((record as any).isAdmin);
    setModalDefaultIsAdmin(isAdminDefault);
    setModalOpen(true);
  };

  const closeResetModal = () => {
    setModalOpen(false);
    setSelectedApplicant(null);
    queryClient.invalidateQueries({ queryKey: ['all-applicants'] });
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  };

  const columns: ColumnsType<ResettableUser> = [
    {
      title: 'Applicant ID',
      dataIndex: 'applicantId',
      key: 'applicantId',
      width: 180,
    },
    {
      title: 'Application No.',
      dataIndex: 'applicationNumber',
      key: 'applicationNumber',
      width: 160,
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    // {
    //   title: 'Gender',
    //   dataIndex: 'genderName',
    //   key: 'genderName',
    // },
    // {
    //   title: 'Address',
    //   dataIndex: 'address',
    //   key: 'address',
    // },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <Button
          icon={<KeyOutlined />}
          size="small"
          onClick={() => openResetModal(record)}
          style={{ backgroundColor: 'darkgreen', color: '#fff', borderColor: 'darkgreen' }}
        >
          Reset Password
        </Button>
      ),
    },
  ];

  return (
    <Card>
      <Title level={4} style={{ color: 'darkgreen', marginBottom: 20 }}>
        User Password Management
      </Title>

    <Row gutter={12} style={{ marginBottom: 16 }}>
      <Col>
        <Input
          style={{ width: 450 }} // or 400
          placeholder="Search by name, email, or application number"
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
        />
      </Col>

      <Col>
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            style={{ backgroundColor: 'darkgreen', borderColor: 'darkgreen' }}
          >
            Search
          </Button>

          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Refresh
          </Button>
        </Space>
      </Col>
    </Row>

      <Table
        rowKey="applicantId"
        columns={columns}
        dataSource={applicants}
        loading={applicantQuery.isLoading || applicantQuery.isFetching}
        pagination={{
          current: pageNumber,
          pageSize: PAGE_SIZE,
          total: applicantQuery.data?.totalCount ?? 0,
          onChange: (page) => setPageNumber(page),
          showSizeChanger: false,
        }}
        scroll={{ x: true }}
      />

      <div style={{ marginTop: 32 }}>
        <Title level={5} style={{ color: 'darkgreen', marginBottom: 16 }}>
          Admin Users
        </Title>
        <Table
          rowKey="applicantId"
          columns={[
            {
              title: 'Admin ID',
              dataIndex: 'applicantId',
              key: 'applicantId',
              width: 180,
            },
            {
              title: 'First Name',
              dataIndex: 'firstName',
              key: 'firstName',
            },
            {
              title: 'Last Name',
              dataIndex: 'lastName',
              key: 'lastName',
            },
            {
              title: 'Email',
              dataIndex: 'email',
              key: 'email',
            },
            {
              title: 'Phone',
              dataIndex: 'phoneNumber',
              key: 'phoneNumber',
            },
            {
              title: 'Status',
              dataIndex: 'isActive',
              key: 'isActive',
              render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'volcano'}>{isActive ? 'Active' : 'Inactive'}</Tag>
              ),
            },
            {
              title: 'Action',
              key: 'action',
              width: 160,
              render: (_, record) => (
                <Button
                  icon={<KeyOutlined />}
                  size="small"
                  onClick={() => openResetModal(record)}
                  style={{ backgroundColor: 'darkgreen', color: '#fff', borderColor: 'darkgreen' }}
                >
                  Reset Password
                </Button>
              ),
            },
          ]}
          dataSource={adminUsers}
          loading={adminUsersQuery.isLoading}
          pagination={false}
          scroll={{ x: true }}
        />
      </div>

      <ResetPasswordModal
        open={modalOpen}
        applicant={selectedApplicant}
        defaultIsAdmin={modalDefaultIsAdmin}
        onClose={closeResetModal}
      />
    </Card>
  );
};

export default ResetAdminPassword;
